import { z } from 'zod';

/**
 * The team roster: players by name, whether or not they have a login, plus
 * attendance and availability. Kept minimal on purpose: most players are minors.
 */

const optText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max} characters.`)
    .nullish()
    .transform((v) => v || null);

const playerName = z.string().trim().min(1, 'Enter the player’s name.').max(80, 'Keep names under 80 characters.');

/** An email for a roster player: the player's own, or a parent's. They're invited to the team and linked to the player. */
export const rosterContact = z.object({
  email: z.string().trim().toLowerCase().max(254, 'That email address is too long.').email('Enter a valid email address.'),
  kind: z.enum(['player', 'parent']),
});
export type RosterContactInput = z.infer<typeof rosterContact>;
export const MAX_ROSTER_CONTACTS = 3;
const rosterContacts = z
  .array(rosterContact)
  .max(MAX_ROSTER_CONTACTS, `Add up to ${MAX_ROSTER_CONTACTS} emails for a player.`)
  .refine((list) => new Set(list.map((c) => c.email)).size === list.length, 'That email is already on this player.');

export const rosterPlayerInput = z.object({
  name: playerName,
  number: optText(3, 'number'),
  position: optText(20, 'position'),
  /** Only coaches and read-only members see this. */
  notes: optText(1000, 'note'),
  contacts: rosterContacts.optional(),
});
export type RosterPlayerInput = z.input<typeof rosterPlayerInput>;

export const updateRosterPlayerInput = z.object({
  name: playerName.optional(),
  number: optText(3, 'number').optional(),
  position: optText(20, 'position').optional(),
  notes: optText(1000, 'note').optional(),
  /** The full list: emails left out are removed (and their invites cancelled). */
  contacts: rosterContacts.optional(),
});

/** Paste one player per line: "12 Mia Chen", "Mia Chen 12", "#7 Ava" or just "Ava". */
export const bulkRosterInput = z.object({ text: z.string().max(5000, 'That list is too long. Add up to about 60 players at a time.') });

export function parseRosterLines(text: string): { name: string; number: string | null; email: string | null }[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.replace(/\t/g, ' ').trim())
    .filter(Boolean)
    .map((line) => {
      // An email anywhere on the line is the player's: take it out, then read the name and number.
      const found = line.match(/<?([^\s<>,;]+@[^\s<>,;]+\.[^\s<>,;]+)>?/);
      const email = found && rosterContact.shape.email.safeParse(found[1]).success ? found[1]!.toLowerCase() : null;
      const l = (found ? line.replace(found[0], ' ') : line).replace(/[\s,;-]+$/, '').replace(/\s+/g, ' ').trim();
      const lead = l.match(/^#?(\d{1,3})[\s.,:-]+(.+)$/);
      if (lead) return { name: lead[2]!.trim(), number: lead[1]!, email };
      const trail = l.match(/^(.+?)[\s,]+#?(\d{1,3})$/);
      if (trail) return { name: trail[1]!.trim(), number: trail[2]!, email };
      return { name: l, number: null, email };
    })
    .filter((p) => p.name.length > 0 && p.name.length <= 80)
    .slice(0, 60);
}

export const rosterLinksInput = z.object({ userIds: z.array(z.string().uuid()).max(6) });

export interface RosterPlayer {
  id: string;
  name: string;
  number: string | null;
  position: string | null;
  /** Coaches and read-only members only. */
  notes: string | null;
  /** Team members linked to this player: the player's own login and/or parents. */
  links: { userId: string; name: string; role: 'player' | 'parent' }[];
  /** Emails for this player, with how their invite is going. Coaches and read-only members only. */
  contacts: RosterContact[];
}

export type RosterContactStatus = 'joined' | 'invited' | 'expired' | 'not_sent' | 'held';
export interface RosterContact {
  id: string;
  email: string;
  kind: 'player' | 'parent';
  /** joined: on the team; invited: invite waiting; expired: invite ran out or was replaced; not_sent: email wasn't set up; held: the team is a draft (sent when it goes live). */
  status: RosterContactStatus;
  invitedAt: string | null;
}

// ---------- Attendance ----------

export const ATTENDANCE = ['present', 'late', 'sitting_out', 'absent'] as const;
export type AttendanceStatus = (typeof ATTENDANCE)[number];
export const ATTENDANCE_LABELS: Record<AttendanceStatus, string> = {
  present: 'Here',
  late: 'Late',
  sitting_out: 'Sitting out',
  absent: 'Absent',
};
/** Counts as turning up, for attendance rates. */
export const ATTENDED: readonly AttendanceStatus[] = ['present', 'late', 'sitting_out'];

export const attendanceInput = z.object({
  marks: z
    .array(z.object({ playerId: z.string().uuid(), status: z.enum(ATTENDANCE).nullable() }))
    .max(80),
});
export type AttendanceInput = z.input<typeof attendanceInput>;

export const availabilityInput = z.object({
  playerId: z.string().uuid(),
  /** True = can't make it. */
  unavailable: z.boolean(),
  reason: optText(200, 'reason'),
});
export type AvailabilityInput = z.input<typeof availabilityInput>;

export interface AttendanceRow {
  player: Pick<RosterPlayer, 'id' | 'name' | 'number' | 'position'>;
  status: AttendanceStatus | null;
  /** Set when a player or parent said they can't make it. */
  unavailable: { reason: string | null; by: string } | null;
}

export interface SessionAttendance {
  rows: AttendanceRow[];
  /** Roster size minus anyone who said they can't make it. */
  expected: number;
  taken: boolean;
  canMark: boolean;
}

/** For players and parents: their own linked players on this session. */
export interface MyAvailability {
  players: { id: string; name: string; unavailable: boolean; reason: string | null; away: { endDate: string; reason: string | null } | null }[];
}

export interface SeasonAttendance {
  /** Sessions in the season where attendance was taken. */
  sessions: { id: string; date: string; title: string }[];
  players: {
    id: string;
    name: string;
    number: string | null;
    marks: Record<string, AttendanceStatus>;
    attended: number;
    /** Attended ÷ sessions taken, 0–1; null if none taken. */
    rate: number | null;
  }[];
}
